/**
 * Adaptador REAL — habla con la frontera de servidor, nunca con Supabase.
 *
 * ⛔ Este archivo NO importa `@supabase/*`, y no debe hacerlo nunca. La SPA
 * llega al dato exclusivamente por `/api/*` (ADR-010). El test V-1 lo verifica.
 *
 * Implementa el mismo `DataPort` que `mockAdapter`, así que las doce vistas
 * no cambian una línea. `mockAdapter` se conserva como doble de prueba y modo
 * demostración.
 *
 * F4.1 implementa `metrics()` y `activity()` — la rebanada que enciende el
 * Dashboard. El resto devuelve un error explícito en vez de una lista vacía:
 * «todavía no está cableado» y «no hay datos» son cosas distintas, y confundirlas
 * es exactamente el defecto que este sistema evita.
 */
import type { DataPort, Loaded } from './port.ts';
import type {
  ActivityEntry, Brief, Cumplimiento, DashboardMetrics, DecisionRecord,
  Deliverable, Integration, PipelineStage, PlanPendiente, Project,
} from '../lib/contracts.ts';

/** Traduce la respuesta HTTP al contrato `Loaded<T>` que esperan las vistas. */
async function get<T>(ruta: string): Promise<Loaded<T>> {
  let res: Response;
  try {
    res = await fetch(ruta, {
      credentials: 'same-origin',   // la cookie HttpOnly viaja sola: mismo origen
      headers: { accept: 'application/json' },
    });
  } catch {
    return { kind: 'error', message: 'sin conexión con el servidor' };
  }

  if (res.status === 401) return { kind: 'error', message: 'sesión expirada' };
  if (res.status === 403) return { kind: 'error', message: 'sin permisos para ver esto' };
  if (!res.ok) return { kind: 'error', message: `el servidor respondió ${res.status}` };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { kind: 'error', message: 'respuesta ilegible del servidor' };
  }

  // Una lista vacía es `empty`, no `ok` con nada adentro.
  if (Array.isArray(body) && body.length === 0) return { kind: 'empty' };
  if (body === null || body === undefined) return { kind: 'empty' };

  return { kind: 'ok', value: body as T };
}


/**
 * `/api/pipeline` trae proyectos y etapas juntos porque la vista los cruza.
 * `stages()` y `projects()` comparten UNA sola request mientras está en vuelo;
 * apenas resuelve, el próximo acceso vuelve a pedir datos frescos.
 */
interface PipelineData { projects: readonly Project[]; stages: readonly PipelineStage[] }
let pipelineInflight: Promise<Loaded<PipelineData>> | null = null;
function pipelineData(): Promise<Loaded<PipelineData>> {
  if (!pipelineInflight) {
    pipelineInflight = get<PipelineData>('/api/pipeline');
    void pipelineInflight.finally(() => { pipelineInflight = null; });
  }
  return pipelineInflight;
}

export const httpAdapter: DataPort = {
  sourceLabel: 'DATOS REALES · nexus-creative-prod',

  metrics:  () => get<DashboardMetrics>('/api/metrics'),
  activity: () => get<readonly ActivityEntry[]>('/api/activity'),

  briefs:       () => get<readonly Brief[]>('/api/briefs/list'),
  projects:     async () => { const r = await pipelineData(); return r.kind === 'ok' ? (r.value.projects.length ? { kind: 'ok' as const, value: r.value.projects } : { kind: 'empty' as const }) : r; },
  stages:       async () => { const r = await pipelineData(); return r.kind === 'ok' ? (r.value.stages.length ? { kind: 'ok' as const, value: r.value.stages } : { kind: 'empty' as const }) : r; },
  pendingPlans: () => get<readonly PlanPendiente[]>('/api/plans/pending'),
  deliverables: () => get<readonly Deliverable[]>('/api/deliverables'),
  integrations: () => get<readonly Integration[]>('/api/integrations'),
  decisions:    () => get<readonly DecisionRecord[]>('/api/decisions'),
  compliance:   () => get<Cumplimiento>('/api/compliance'),
};

/* ── Escritura ───────────────────────────────────────────────────────────── */

export interface PedidoEnviado {
  readonly code: string;
  readonly projectCode: string | null;
  readonly ruta: string;
  readonly motivo: string;
  readonly desde: string | null;
  readonly hasta: string | null;
  /** `null` = no se pudo interpretar el plazo pedido. Distinto de «no llegamos». */
  readonly llegamos: boolean | null;
  /** Distingue «llegamos justo» de «llegamos tranquilos». */
  readonly holgura: 'comoda' | 'ajustada' | 'insuficiente' | null;
  /** `plan_propuesto` | `sin_estimar`. Se lee ANTES que cualquier fecha. */
  readonly estado: 'plan_propuesto' | 'sin_estimar';
  /**
   * El plan a aprobar: qué se entrega, con qué plataformas, en qué pasos y en
   * cuánto tiempo. Sin aprobación no hay fecha ni ejecución.
   */
  readonly plan: {
    readonly entregable: string;
    readonly nivel: string;
    readonly complejidad: 'baja' | 'media' | 'alta';
    readonly plataformas: readonly string[];
    readonly roadmap: ReadonlyArray<{ readonly label: string; readonly min: number; readonly max: number }>;
    readonly minutos: number;
    readonly requiereExtension: boolean;
    readonly extraMin: number;
    readonly justificacion: string | null;
    readonly alcance: string | null;
  } | null;
  readonly vence: string | null;
  readonly veredicto: string;
}

/**
 * Envía el pedido. Devuelve el código asignado, o el mensaje del servidor.
 *
 * El código lo genera la base, no el cliente: es lo que el usuario va a
 * mencionar cuando pregunte por su pedido.
 */
export async function enviarBrief(datos: {
  title: string; need: string; audience: string; deadline: string;
}): Promise<PedidoEnviado | string> {
  let res: Response;
  try {
    res = await fetch('/api/briefs', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(datos),
    });
  } catch {
    return 'sin conexión con el servidor';
  }
  const body = (await res.json().catch(() => ({}))) as Partial<PedidoEnviado> & { error?: string };
  if (!res.ok) return body.error ?? `el servidor respondió ${res.status}`;
  return body.code && body.veredicto ? (body as PedidoEnviado) : 'respuesta inesperada del servidor';
}

/* ── Sesión ──────────────────────────────────────────────────────────────── */

export interface Sesion {
  readonly displayName: string;
  readonly role: 'owner' | 'operator' | 'viewer';
}

export async function login(email: string, password: string): Promise<Sesion | string> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as Partial<Sesion> & { error?: string };
  if (!res.ok) return body.error ?? 'no se pudo iniciar sesión';
  if (!body.displayName || !body.role) return 'respuesta inesperada del servidor';
  return { displayName: body.displayName, role: body.role };
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
}

/**
 * Pide el correo de recuperación. Devuelve SIEMPRE el mismo mensaje, exista o no
 * la cuenta: el servidor no confirma quién tiene usuario y el cliente tampoco.
 */
export async function pedirRecuperacion(email: string): Promise<string> {
  const res = await fetch('/api/auth/recover', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const body = (await res.json().catch(() => ({}))) as { mensaje?: string; error?: string };
  return body.mensaje ?? body.error ?? 'no se pudo pedir el enlace';
}

/**
 * Fija la contraseña nueva con el código que vino en el correo.
 * `null` = salió bien; una cadena = el motivo por el que no.
 */
export async function fijarContrasena(token: string, password: string): Promise<string | null> {
  const res = await fetch('/api/auth/password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  return res.ok && body.ok ? null : (body.error ?? 'no se pudo cambiar la contraseña');
}

/** `null` = no hay sesión. Es lo que la SPA consulta al arrancar. */
export async function me(): Promise<Sesion | null> {
  const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
  if (!res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as Partial<Sesion>;
  return body.displayName && body.role
    ? { displayName: body.displayName, role: body.role }
    : null;
}

/** Resultado de aprobar o rechazar el plan. */
export interface PlanResuelto {
  readonly projectCode: string;
  /** 'plan_aprobado' | 'plan_rechazado' | 'contrapropuesta'. */
  readonly estado: string;
  /** Sólo hay fecha si se aprobó. Rechazar no deja compromiso. */
  readonly hasta: string | null;
  /** Presente sólo cuando `estado === 'contrapropuesta'`: el plan reducido. */
  readonly plan?: {
    readonly entregable: string;
    readonly nivel: string;
    readonly complejidad: 'baja' | 'media' | 'alta';
    readonly plataformas: readonly string[];
    readonly roadmap: ReadonlyArray<{ readonly label: string; readonly min: number; readonly max: number }>;
    readonly minutos: number;
    readonly requiereExtension: boolean;
    readonly extraMin: number;
    readonly justificacion: string | null;
    readonly alcance: string | null;
  };
}

/**
 * Registra la decisión del usuario sobre una extensión.
 * Devuelve un mensaje de error si algo falló, para que la vista lo muestre.
 */
export async function resolverPlan(
  projectCode: string, decision: 'aprobar' | 'rechazar',
): Promise<PlanResuelto | string> {
  const res = await fetch('/api/plan', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ projectCode, decision }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) return typeof body['error'] === 'string' ? body['error'] : 'no se pudo registrar tu decisión';

  // Se VERIFICA la forma en vez de castear a ciegas. Un `as unknown as` acá
  // hacía que una respuesta inesperada explotara más tarde y en otro lado, con
  // un error que no señalaba el origen. Si el servidor cambia el contrato, se
  // entera acá y lo dice.
  if (typeof body['estado'] !== 'string') {
    return 'el servidor respondió algo que no es un plan resuelto';
  }
  return body as unknown as PlanResuelto;
}

/**
 * Arranca la generación del entregable. El worker corre en segundo plano y
 * escribe la pieza cuando termina; acá sólo se dispara (202) y se vuelve. El
 * resultado se ve consultando los entregables, no esperando esta promesa.
 */
export async function generarEntregable(projectCode: string): Promise<'ok' | string> {
  try {
    const res = await fetch('/.netlify/functions/generate-background', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ projectCode }),
    });
    // 202 = aceptado y corriendo. 200 también (si el entorno lo corre síncrono).
    if (res.status === 202 || res.ok) return 'ok';
    if (res.status === 401) return 'sesión expirada';
    if (res.status === 403) return 'sin permisos para generar';
    return `el motor no arrancó (${res.status})`;
  } catch {
    return 'sin conexión con el motor';
  }
}

export interface Simulacion {
  readonly ruta: string; readonly rutaNombre: string; readonly motivo: string;
  readonly estado: string; readonly veredicto: string;
  readonly plazoLeido: string | null;
  readonly plan: {
    entregable: string; nivel: string; complejidad: string;
    plataformas: readonly string[]; minutos: number;
    roadmap: readonly { label: string; min: number }[];
    alcance?: string | null; justificacion?: string | null;
  } | null;
}

/**
 * Corre el Decision Engine sin comprometer nada.
 *
 * No escribe: no crea brief ni proyecto ni auditoría. Es lo que un simulador
 * tiene que ser — si escribiera algo, dejaría de serlo.
 */
export async function simular(texto: string, plazo: string): Promise<Simulacion | string> {
  const res = await fetch('/api/simulate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ texto, plazo }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) return typeof body['error'] === 'string' ? body['error'] : 'no se pudo simular';
  if (typeof body['ruta'] !== 'string') return 'el servidor respondió algo que no es una simulación';
  return body as unknown as Simulacion;
}
